import { useMutation, UseMutationResult } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { downloadBlob, downloadFile } from "@/shared/domain/files/filesystem";

/** The type the backend serves non-renderable artifacts as, with `attachment` disposition. */
const ATTACHMENT_CONTENT_TYPE = "application/octet-stream";

/**
 * Save one tool-produced artifact, minting a fresh presigned URL (5-minute TTL) on every run.
 *
 * Attachments are followed with an anchor click: the URL arrives after an await, outside the
 * click's user activation, where Safari silently blocks `window.open`. Inline types (images) are
 * fetched and saved from an object URL, since `download` is ignored cross-origin (needs CORS).
 */
export function useDownloadArtifact(
  threadId: string,
  toolUseId: string,
  artifactName: string,
): UseMutationResult<void, Error, void> {
  const { agentThreadService } = useDomainServices();

  return useMutation<void, Error, void>({
    mutationFn: async () => {
      const minted = await agentThreadService.getArtifactUrl(
        threadId,
        toolUseId,
        artifactName,
      );
      if (minted.content_type === ATTACHMENT_CONTENT_TYPE) {
        downloadFile(artifactName, minted.url);
        return;
      }
      const content = await agentThreadService.getArtifactContent(minted.url);
      downloadBlob(artifactName, content);
    },
  });
}
